import { useContext, useState, useCallback } from 'react';
import { AppContext } from 'contexts/AppContext';
import { apiPost } from './apiPost';

/**
 * Data access for the Voice tab.
 *
 * /send/voice-summary   → headline figures
 * /send/voice-sessions  → one row per session, newest first
 *
 * There is no voice table. A voice turn is written to the same interactions
 * and messages rows a text turn is, and the voice facts ride `metadata` jsonb —
 * which is why this feature needed no migration and no app release.
 *
 * THREE FIELDS EXIST TO KEEP AN ABSENCE HONEST, and the tab must not collapse
 * any of them to a zero:
 *
 *   output_tokens_recorded    How many turns reported output tokens at all. The
 *                             Live API populates no output field, so a turn with
 *                             none is normal rather than free.
 *
 *   output_tokens_derived_turns
 *                             How many of those were INFERRED as total - prompt
 *                             rather than measured. adk-v2 #105 added the
 *                             derivation and has always flagged it; nothing read
 *                             the flag until now, so the token figure was shown
 *                             as if measured. A count of 0 means every figure was
 *                             reported directly; a count equal to the recorded
 *                             count means all of it is subtraction.
 *
 *   barge_in_recorded_turns   0 barge-ins out of 0 recorded means the field was
 *                             never captured. 0 out of 40 means nobody
 *                             interrupted. Those are different facts.
 *
 * No platform filter is sent: voice is mobile-only today, and a filter that
 * silently matched nothing would look like "no voice traffic".
 */

const PAGE_SIZE = 50;

export const useVoiceData = () => {
  const {
    dateRange, selectedClient, getAccessTokenSilently, base_url, environmentFilter,
  } = useContext(AppContext);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  const post = useCallback(
    (path, body) => apiPost(base_url, getAccessTokenSilently, path, body),
    [base_url, getAccessTokenSilently],
  );

  // dateRange is an ARRAY [from, to] of Dates, not {from,to} — AppContext.js:152.
  const range = useCallback(() => {
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    if (!from || !to || !selectedClient?.key) return null;
    return {
      start_date: new Date(from).toISOString().split('T')[0],
      end_date: new Date(to).toISOString().split('T')[0],
      client: selectedClient.key,
      // Environment IS sent, unlike platform: voice rows carry the same
      // environment column every other figure now filters on.
      environment: environmentFilter || 'all',
    };
  }, [dateRange, selectedClient, environmentFilter]);

  const fetchSummary = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await post('voice-summary',
        { ...base, exclude_internal: excludeInternal, internal_only: internalOnly });
      setSummary(data?.data || null);
    } catch (err) {
      setSummaryError(err.response?.data?.detail || err.message || 'Failed to load voice metrics');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [range, post]);

  // A FAILED request and an EMPTY result must not render the same way. The
  // entry-points card once swallowed a 500 into an empty array and told the
  // reader there had been no widget opens — a false finding, not a broken card.
  // Voice is especially exposed to that: an empty list is the EXPECTED state
  // before the first voice release, so "no sessions" must never be able to mean
  // "the request failed".
  const fetchSessions = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await post('voice-sessions',
        { ...base, limit: PAGE_SIZE, exclude_internal: excludeInternal, internal_only: internalOnly });
      setSessions(data?.data || []);
    } catch (err) {
      setSessionsError(err.response?.data?.detail || err.message || 'Failed to load voice sessions');
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [range, post]);

  return {
    summary, summaryLoading, summaryError, fetchSummary,
    sessions, sessionsLoading, sessionsError, fetchSessions,
  };
};
